import { useMemo, useEffect, useState, useRef } from 'react';
import { Theme, useTheme } from '@mui/material/styles';
import Delete from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import Info from '@mui/icons-material/Info';
import Switch from '@mui/material/Switch';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import SvgIcon from '@mui/material/SvgIcon';
import TextField from '@mui/material/TextField';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ContentCopy from '@mui/icons-material/ContentCopy';
import { DragDropProvider } from '@dnd-kit/react';
import dayjs from 'dayjs';
import {
  GRID_TREE_DATA_GROUPING_FIELD,
  GridAggregationModel,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridSortModel,
} from '@mui/x-data-grid-premium';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation, TFunction } from 'react-i18next';
import Draggable from 'react-draggable';
import { ReactComponent as editDatagridIcon } from '../../../../assets/editDatagridIcon.svg';
import {
  addSectionButtonContainerSx,
  addSectionButtonSx,
  customReportDataGridContainerSx,
  customReportDataGridOutlineSx,
  customReportDataGridSx,
  draggablePopoverBackdropSx,
  editMenuContainerLargeSx,
  editMenuHalfMarginBelow,
  editMenuMarginBelow,
  editMenuTitleSx,
  errorSx,
  gridContainerSx,
  iconSx,
  sectionNameContainerSx,
} from '../styles';
import SelectDataset from '../../CentralReports/components/SelectDataset';
import DataGrid, { BaseGridRow } from '../../../components/DataGrid/DataGrid';
import { yesterday } from '../../../utils/reportingUtils/filterUtils';
import { NonDraggableSelectors, getColumnConfig } from '../helpers';
import { useExecuteCustomReportQuery } from '../../requests/queries';
import { ColumnDefinition, ComparativeColumn, DatagridElement, GroupedColumn } from '../../requests/index';
import { ReportFiltersFormValues } from '../types';
import { DatasetColumn } from '../../CentralReports/components/CreateReportDialog';
import { datasetNameMap } from '../../CentralReports/utils/EditReportUtils';
import { datasetCheckboxAndSwitchSx } from '../../CentralReports/styles';
import { getTreeDataPath } from '../../../utils/reportingUtils/datagridUtils';
import CustomTreeGroupCell from '../../../components/DataGrid/CustomCells/CustomTreeGroupCell';
import { ComparativeCard, DraggableCard, DroppableZone, GroupingCard } from './DraggableCards';
import { getPeriods, createDateRangeMap } from './DateUtils';
import { useFilters } from '../../../contexts/filterContext';

interface CustomReportsGridSectionProps {
  isEditMode: boolean;
  canEdit: boolean;
  sectionIndex: number;
  canMoveDown: boolean;
  canDelete: boolean;
  element: DatagridElement;
  sectionName: string | undefined;
  columns: GridColDef[];
  allColumns: Record<string, ColumnDefinition[]>;
  filterValues: ReportFiltersFormValues | undefined;
  selectedColumns: ColumnDefinition[];
  datasetName: string;
  showSummaryRow: boolean;
  aggregationModel: GridAggregationModel;
  exportCustomReportMutation: { isLoading: boolean };
  exportCustomReportsGridData: () => void;
  handleSetActiveSection: (section: number | '') => void;
  handleUpdateSection: (
    sectionIndex: number,
    sectionDataset: string,
    newColumns: DatasetColumn[],
    groupedColumns: GroupedColumn[],
    comparativeColumns: ComparativeColumn[],
    sectionName: string,
    showSummaryRow: boolean,
    sortBy: string,
    sortOrder: 'asc' | 'desc'
  ) => void;
  handleMoveSection: (sectionNum: number, direction: 'up' | 'down') => void;
  handleDeleteSection: (sectionNum: number) => void;
  handleAddSection: (sectionData: DatagridElement | undefined, addAt: number) => void;
  setAddAbove: (above: boolean) => void;
  setAddSectionAt: (section: number) => void;
  setShowCreateModal: (open: boolean) => void;
}

interface SectionHeaderWithAddButtonProps {
  title: string;
  tooltipText: string;
  buttonTooltipText: string;
  isButtonDisabled: boolean;
  buttonTestId: string;
  onButtonClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  theme: Theme;
  t: TFunction;
}

function SectionHeaderWithAddButton({
  title,
  tooltipText,
  buttonTooltipText,
  isButtonDisabled,
  buttonTestId,
  onButtonClick,
  theme,
  t,
}: SectionHeaderWithAddButtonProps) {
  return (
    <Grid container alignItems='center' justifyContent='space-between' sx={editMenuHalfMarginBelow}>
      <Grid container alignItems='center' gap={1} sx={{ width: 'auto' }}>
        <Typography sx={editMenuTitleSx}>{title}</Typography>
        <Tooltip title={tooltipText} placement='right'>
          <Info fontSize='small' sx={{ color: 'text.secondary', marginTop: '15px' }} />
        </Tooltip>
      </Grid>
      <Tooltip title={buttonTooltipText} disableHoverListener={!isButtonDisabled} placement='left'>
        <span>
          <Button disabled={isButtonDisabled} data-testid={buttonTestId} onClick={onButtonClick}>
            {t('buttonText.add')}
          </Button>
        </span>
      </Tooltip>
    </Grid>
  );
}

function CustomReportsGridSection({
  isEditMode,
  canEdit,
  sectionIndex,
  canMoveDown,
  canDelete,
  sectionName,
  element,
  allColumns,
  showSummaryRow,
  columns,
  filterValues,
  selectedColumns,
  datasetName,
  aggregationModel,
  exportCustomReportMutation,
  exportCustomReportsGridData,
  handleSetActiveSection,
  handleAddSection,
  handleUpdateSection,
  handleMoveSection,
  handleDeleteSection,
  setAddAbove,
  setAddSectionAt,
  setShowCreateModal,
}: CustomReportsGridSectionProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const nodeRef = useRef(null);
  const { fiscalCalendarFilters } = useFilters();
  const periods = useMemo(() => getPeriods(fiscalCalendarFilters?.fiscalCalendar), [fiscalCalendarFilters]);
  const dateRangeToStartEndDateMap = useMemo(() => createDateRangeMap(periods), [periods]);
  const isDarkMode = theme.palette.mode === 'dark';
  const [isActive, setIsActive] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 3 });
  const [editDatagridButtonMenuAnchorEl, setEditDatagridButtonMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [editDatagridMenuAnchorEl, setEditDatagridMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [addGroupingMenuAnchorEl, setAddGroupingMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [addComparisonMenuAnchorEl, setAddComparisonMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [editShowSummaryRow, setEditShowSummaryRow] = useState<boolean>(showSummaryRow);
  const [editSectionNameValue, setEditSectionNameValue] = useState<string>(sectionName ?? '');
  const [editDatagridValue, setEditDatagridValue] = useState<DatasetColumn[]>([]);
  const [groupedColumns, setGroupedColumns] = useState<GroupedColumn[]>(element?.groupedColumns?.[datasetName] ?? []);
  const [comparativeColumns, setComparativeColumns] = useState<ComparativeColumn[]>(
    element?.comparativeColumns?.[datasetName] ?? []
  );
  const [selectedDatasetId, setSelectedDatasetId] = useState<string | null>(null);
  const [editSortBy, setEditSortBy] = useState<string>('');
  const [editSortOrder, setEditSortOrder] = useState<'asc' | 'desc'>('asc');

  // Helper function to get the correct sort model based on whether the field is grouped
  const getSortModelFromDefaultSort = (
    defaultSort: Array<{ field: string; sortDirection: 'asc' | 'desc' }> | undefined,
    grouped: GroupedColumn[]
  ) => {
    if (!defaultSort || defaultSort.length === 0) return [];

    // If the sort field is a grouped column, use the special tree grouping field
    const isGroupedColumn = grouped.some((col) => col.field === defaultSort[0].field);
    const sortField = isGroupedColumn ? GRID_TREE_DATA_GROUPING_FIELD : defaultSort[0].field;

    return [{ field: sortField, sort: defaultSort[0].sortDirection }];
  };

  const orderColumnsWithComparatives = (
    cols: GridColDef[],
    comparativeFields: Array<GridColDef & { baseField: string }>,
    grouped: GroupedColumn[]
  ): GridColDef[] => {
    // Group comparative fields by their base field
    const comparativeByBaseField = comparativeFields.reduce((acc, compCol) => {
      const base = compCol.baseField;
      if (!acc[base]) {
        acc[base] = [];
      }
      acc[base].push(compCol);
      return acc;
    }, {} as Record<string, Array<(typeof comparativeFields)[0]>>);

    // Build ordered columns: insert comparative columns right after their base column
    const baseColumns = cols.filter(
      (col) => col.field && !grouped.some((dimCol: GroupedColumn) => dimCol.field === col.field)
    );

    const orderedColumns: GridColDef[] = [];
    baseColumns.forEach((col) => {
      orderedColumns.push(col);
      // Add comparative columns for this base field immediately after the base column
      if (col.field && comparativeByBaseField[col.field]) {
        comparativeByBaseField[col.field].forEach((compCol) => {
          // Omit baseField property when pushing to orderedColumns
          const { baseField, ...gridColDef } = compCol;
          orderedColumns.push(gridColDef as GridColDef);
        });
      }
    });

    return orderedColumns;
  };

  const [sortModel, setSortModel] = useState<GridSortModel>(
    getSortModelFromDefaultSort(element?.defaultSort, element?.groupedColumns?.[datasetName] ?? [])
  );

  const { executeCustomReportResponse, setExecuteCustomReportRequest, isExecuteCustomReportLoading } =
    useExecuteCustomReportQuery();

  useEffect(() => {
    const newGroupedColumns = element?.groupedColumns?.[datasetName] ?? [];
    setGroupedColumns(newGroupedColumns);
    setSortModel(getSortModelFromDefaultSort(element?.defaultSort, newGroupedColumns));
    setComparativeColumns(element?.comparativeColumns?.[datasetName] ?? []);
  }, [element, datasetName]);

  useEffect(() => {
    if (editDatagridValue.length === 0) {
      setEditSortBy('');
      return;
    }

    const nonCustomMetricCols = editDatagridValue.filter((col) => !col.optionId.startsWith('custommetric_'));
    const sortByExists = editDatagridValue.some((col) => col.label === editSortBy);
    const sortByIsCustomMetric =
      editSortBy !== '' &&
      editDatagridValue.some((col) => col.label === editSortBy && col.optionId.startsWith('custommetric_'));

    if (!editSortBy || !sortByExists) {
      // Sort field gone entirely — pick first non-custom-metric column, fall back to first column
      setEditSortBy((nonCustomMetricCols[0] ?? editDatagridValue[0]).label);
    } else if (sortByIsCustomMetric && nonCustomMetricCols.length > 0) {
      // Sort field is a custom metric but a regular column is now available — switch to it
      setEditSortBy(nonCustomMetricCols[0].label);
    }
  }, [editDatagridValue, editSortBy]);

  useEffect(() => {
    if (!editSortBy) return;

    // Determine if the sort field is a grouped column
    const isGroupedColumn = groupedColumns.some((col) => col.field === editSortBy);
    const sortField = isGroupedColumn ? GRID_TREE_DATA_GROUPING_FIELD : editSortBy;

    // Update the sort model to trigger immediate grid re-sort
    setSortModel([{ field: sortField, sort: editSortOrder }]);
  }, [editSortBy, editSortOrder, groupedColumns]);

  // Used to prevent extra API calls when columns are reordered without content changes
  const comparativeColumnsKey = useMemo(
    () =>
      comparativeColumns
        .map((col) => `${col.field}_${col.dateRange}`)
        .sort((a, b) => a.localeCompare(b))
        .join('|'),
    [comparativeColumns]
  );

  // Used to prevent extra API calls when the column array gets a new reference but the content hasn't changed
  const selectedColumnsKey = useMemo(
    () =>
      selectedColumns
        ?.map((col) => `${col.columnName}_${col.customMetricId ?? ''}`)
        .sort((a, b) => a.localeCompare(b))
        .join('|') ?? '',
    [selectedColumns]
  );

  useEffect(() => {
    if (!filterValues || !selectedColumns || selectedColumns.length === 0 || filterValues.sites.length === 0) return;

    const siteIds = filterValues.sites.map((site) => site.id ?? '');
    const selectedDataColumns = selectedColumns
      .filter((col: ColumnDefinition) => !col.customMetricId)
      .map((col: ColumnDefinition) => col.columnName);
    const customMetricIds = selectedColumns
      .map((col: ColumnDefinition) => col.customMetricId)
      .filter((metricId): metricId is string => Boolean(metricId));

    // Make sure comparisons have their base column included in the request
    comparativeColumns?.forEach((col) => {
      if (!selectedDataColumns.includes(col.field)) {
        selectedDataColumns.push(col.field);
      }
    });

    setExecuteCustomReportRequest({
      detailsAndConfig: {
        data: {
          [datasetName]: {
            selectedColumns: selectedDataColumns,
            ...(customMetricIds.length > 0 ? { customMetricIds } : {}),
          },
        },
        additionalFilters: [],
        elements: [],
      },
      queryRequest: {
        startDate: filterValues.dateRange.startDate ?? yesterday,
        endDate: filterValues.dateRange.endDate ?? yesterday,
        siteIds,
        // BE currently throwing error with empty comparativeColumns array
        ...(comparativeColumns?.length > 0
          ? {
              comparativeColumns: comparativeColumns.map((col) => ({
                field: col.field,
                dateRange: col.dateRange,
                startDate: dateRangeToStartEndDateMap[col.dateRange]?.startDate,
                endDate: dateRangeToStartEndDateMap[col.dateRange]?.endDate,
              })),
            }
          : {}),
      },
    });
    // Using comparativeColumnsKey and selectedColumnsKey instead of
    // comparativeColumns and selectedColumns to prevent API calls when arrays get new references but content hasn't changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterValues,
    setExecuteCustomReportRequest,
    comparativeColumnsKey,
    selectedColumnsKey,
    datasetName,
    dateRangeToStartEndDateMap,
  ]);

  const getAvailableDateRangesForField = (field: string, index: number) =>
    Object.keys(dateRangeToStartEndDateMap).filter((key) => {
      // Disable date range options that would result in duplicate comparative columns
      const potentialField = `${field}_${key}`;
      return !comparativeColumns.some(
        (existingCol, i) => `${existingCol.field}_${existingCol.dateRange}` === potentialField && i !== index
      );
    });

  const dimensionColumns = useMemo(() => {
    const nonFactColumns = allColumns[datasetName]?.filter((col) => !col.isNumeric) ?? [];
    return columns.filter((col) => nonFactColumns.some((dimCol) => dimCol.columnName === col.field));
  }, [allColumns, datasetName, columns]);

  const factColumns = useMemo(() => {
    const factCols = allColumns[datasetName]?.filter((col) => col.isNumeric) ?? [];
    return factCols.filter(
      (col) =>
        columns.some((factCol) => factCol.field === col.columnName) ||
        editDatagridValue?.some((c) => c.label === col.columnName)
    );
  }, [allColumns, editDatagridValue, datasetName, columns]);

  const availableFactColumnsForComparison = useMemo(
    () =>
      factColumns.filter(
        (col) => comparativeColumns.filter((dimCol) => dimCol.field === col.columnName).length !== periods.length
      ),
    [factColumns, comparativeColumns, periods]
  );

  const filteredColumns = useMemo(() => {
    const comparativeFields = comparativeColumns.map((col) => {
      const dates = dateRangeToStartEndDateMap[col.dateRange];
      const columnConfig = getColumnConfig(datasetName, col.field);
      const displayName = columnConfig?.headerName || col.field;
      const baseColumn = columns.find((c) => c.field === col.field);

      return {
        field: `${col.field}_${col.dateRange}`,
        headerName: displayName,
        baseField: col.field, // Track which base column this comparison belongs to
        width: 250,
        type: baseColumn?.type ?? ('number' as const),
        // Inherit formatting properties from base column
        ...(baseColumn?.valueFormatter && { valueFormatter: baseColumn.valueFormatter }),
        ...(baseColumn?.renderCell && { renderCell: baseColumn.renderCell }),
        ...(baseColumn?.valueGetter && { valueGetter: baseColumn.valueGetter }),
        renderHeader: () => (
          <Grid container direction='column' sx={{ alignItems: 'flex-end' }}>
            <Typography variant='body2' fontWeight={500}>
              {displayName}
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              {/* For single date */}
              {dates?.date && `${dayjs(dates.date).format('MM/DD/YYYY')}`}

              {/* For date ranges */}
              {!dates?.date &&
                dates?.startDate &&
                dates?.endDate &&
                `${dayjs(dates.startDate).format('MM/DD/YYYY')} - ${dayjs(dates.endDate).format('MM/DD/YYYY')}`}
            </Typography>
          </Grid>
        ),
      };
    });

    return orderColumnsWithComparatives(columns, comparativeFields, groupedColumns);
  }, [columns, comparativeColumns, groupedColumns, datasetName, dateRangeToStartEndDateMap]);

  const rows: BaseGridRow[] = useMemo(() => {
    if (!executeCustomReportResponse) return [];

    const rowsWithoutHierarchy = executeCustomReportResponse.results.map((rowData, rowIndex) => {
      const row: BaseGridRow = { id: rowIndex + 1, ...rowData };
      return row;
    });

    if (groupedColumns.length === 0) return rowsWithoutHierarchy;

    const rowsWithHierarchy = rowsWithoutHierarchy.map((row) => {
      const hierarchy = groupedColumns.reduce((acc, col) => {
        acc.push(String(row[col.field] ?? ''));
        return acc;
      }, [] as string[]);

      hierarchy.push(String(row.id));

      return { ...row, hierarchy };
    });

    return rowsWithHierarchy;
  }, [executeCustomReportResponse, groupedColumns]);

  const aggregationModelWithComparative: GridAggregationModel = useMemo(
    () => ({
      ...aggregationModel,
      ...comparativeColumns.reduce((acc, col) => {
        acc[`${col.field}_${col.dateRange}`] = 'sum';
        return acc;
      }, {} as GridAggregationModel),
    }),
    [aggregationModel, comparativeColumns]
  );

  const addSectionButton = (position: 'above' | 'below') => (
    <Button
      variant='outlined'
      sx={addSectionButtonSx(position)}
      onClick={() => {
        setAddAbove(position === 'above');
        setAddSectionAt(sectionIndex);
        setShowCreateModal(true);
      }}
      startIcon={<AddIcon />}
    >
      {t('admin.customReports.editReports.addSection')}
    </Button>
  );

  const handleCloseEditDatagridMenu = (_event?: unknown, reason?: 'backdropClick' | 'escapeKeyDown') => {
    // Drop focus from any element in the edit menu before closing
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    setEditDatagridButtonMenuAnchorEl(null);
    setEditDatagridMenuAnchorEl(null);

    // Update grouped columns: only set sortDirection from defaultSort if the field matches
    const updatedGroupedColumns = groupedColumns.map((col) =>
      col.field === editSortBy ? { ...col, sortDirection: editSortOrder } : { ...col, sortDirection: undefined }
    );

    handleUpdateSection(
      sectionIndex,
      datasetName,
      editDatagridValue,
      updatedGroupedColumns,
      comparativeColumns,
      editSectionNameValue,
      editShowSummaryRow,
      editSortBy,
      editSortOrder
    );

    // On keyboard experience reset focus to edit button for improved accessibility, on mouse experience reset isActive to false
    if (reason !== 'escapeKeyDown') {
      setIsActive(false);
    } else {
      setTimeout(() => {
        const editButton = document.getElementById(`edit-datagrid-button-${sectionIndex + 1}`);
        if (editButton) {
          editButton.focus();
        }
      }, 0);
    }
  };

  const handleOpenEditDatagridMenu = (e: React.MouseEvent<HTMLElement>) => {
    if (!selectedColumns) {
      setEditDatagridValue([]); // Clear columns for this section
      return;
    }

    const mapEntries = datasetNameMap ? Object.entries(datasetNameMap) : [];
    const dsId = mapEntries.find(([key, value]) => value === datasetName)?.[0];
    const normalizedDatasetName = datasetName.replace(/\s/g, '').toLowerCase();

    if (!dsId) return;

    const datagridColumnNames = element.columns?.[datasetName];

    const columnByName = new Map(allColumns[datasetName]?.map((col) => [col.columnName, col]));
    const chosenCols = datagridColumnNames
      .map((colName: string) => columnByName.get(colName))
      .filter((col: ColumnDefinition | undefined): col is ColumnDefinition => col !== undefined);

    setEditDatagridValue(
      chosenCols.map((col: ColumnDefinition) => ({
        datasetId: dsId,
        optionId: col.customMetricId
          ? `custommetric_${col.customMetricId}`
          : `${normalizedDatasetName}_${String(col.id)}`,
        label: col.columnName,
        id: `${dsId}.${
          col.customMetricId ? `custommetric_${col.customMetricId}` : `${normalizedDatasetName}_${String(col.id)}`
        }`,
        filterType: col.filterType,
      }))
    );

    setSelectedDatasetId(dsId);

    if (element.defaultSort && element.defaultSort.length > 0) {
      setEditSortBy(element.defaultSort[0].field);
      setEditSortOrder(element.defaultSort[0].sortDirection);
    } else if (chosenCols.length > 0) {
      // Default to first column if no defaultSort exists
      setEditSortBy(chosenCols[0].columnName);
      setEditSortOrder('asc');
    } else {
      setEditSortBy('');
      setEditSortOrder('asc');
    }

    setEditDatagridButtonMenuAnchorEl(e.currentTarget);
  };

  const handleOnMouseOver = () => {
    if (!isEditMode || !canEdit) return;
    handleSetActiveSection(sectionIndex);
    setIsActive(true);
  };

  const handleOnMouseLeave = () => {
    if (editDatagridButtonMenuAnchorEl || editDatagridMenuAnchorEl) return; // Keep section active if either menu is open
    handleSetActiveSection('');
    setIsActive(false);
  };

  const handleOnBlur = (e: React.FocusEvent<HTMLElement>) => {
    // Keep active if focus is moving to an element within the section, ex: the edit button or the top add section button
    if (editDatagridButtonMenuAnchorEl || editDatagridMenuAnchorEl || e.currentTarget.contains(e.relatedTarget as Node))
      return;
    handleSetActiveSection('');
    setIsActive(false);
  };

  const handleAddGroupingColumn = (col: string) => {
    const newGroupedCol: GroupedColumn = { field: col };
    setGroupedColumns((prev) => [...prev, newGroupedCol]);
    setAddGroupingMenuAnchorEl(null);
  };

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);

    // If user clicked sort on the grouped column, update the grouped column's sort direction
    if (
      newSortModel.length > 0 &&
      newSortModel[0].field === GRID_TREE_DATA_GROUPING_FIELD &&
      groupedColumns.length > 0
    ) {
      const updatedGroupedColumns = groupedColumns.map((col, index) =>
        index === 0
          ? { ...col, sortDirection: newSortModel[0].sort as 'asc' | 'desc' }
          : { ...col, sortDirection: undefined }
      );
      setGroupedColumns(updatedGroupedColumns);
    }
  };

  const handleAddComparativeColumn = (col: string) => {
    const availableDates = getAvailableDateRangesForField(col, -1); // Log available date ranges for debugging

    if (availableDates.length > 0) {
      const newComparativeCol: ComparativeColumn = { field: col, dateRange: availableDates[0] };
      setComparativeColumns((prev) => [...prev, newComparativeCol]);
    }

    setAddComparisonMenuAnchorEl(null);
  };

  const groupingColDef = {
    headerName: groupedColumns.length === 1 ? groupedColumns[0].field : '',
    minWidth: 400,
    sortable: true,
    filterable: false,
    renderCell: (params: GridRenderCellParams) => (
      <CustomTreeGroupCell
        {...{
          ...params,
          hideFinalCellValue: true,
          showDescendantCount: true,
          countDescendantLeafNodes: true,
          offsetMultiplier: 4,
        }}
      />
    ),
  };

  return (
    <>
      {/* Dropdown for section options: edit, duplicate, delete */}
      <Menu
        anchorEl={editDatagridButtonMenuAnchorEl}
        open={Boolean(editDatagridButtonMenuAnchorEl)}
        onClose={() => setEditDatagridButtonMenuAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            setEditShowSummaryRow(showSummaryRow);
            setEditDatagridMenuAnchorEl(editDatagridButtonMenuAnchorEl);
            setEditDatagridButtonMenuAnchorEl(null);
          }}
          data-testid='edit-datagrid-button'
        >
          <ListItemIcon>
            <SvgIcon viewBox='0 0 20 20' component={editDatagridIcon} sx={iconSx} />
          </ListItemIcon>
          <ListItemText>{t('admin.customReports.editReports.editDataTable')}</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleAddSection(undefined, sectionIndex);
            setEditDatagridButtonMenuAnchorEl(null);
          }}
          data-testid='duplicate-section-button'
        >
          <ListItemIcon>
            <ContentCopy />
          </ListItemIcon>
          <ListItemText>{t('buttonText.duplicate')}</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleMoveSection(sectionIndex, 'up');
            setEditDatagridButtonMenuAnchorEl(null);
          }}
          disabled={sectionIndex === 0}
          data-testid='move-section-up-button'
        >
          <ListItemIcon>
            <ArrowUpward />
          </ListItemIcon>
          <ListItemText>{t('admin.customReports.editReports.moveUp')}</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleMoveSection(sectionIndex, 'down');
            setEditDatagridButtonMenuAnchorEl(null);
          }}
          data-testid='move-section-down-button'
          disabled={!canMoveDown}
        >
          <ListItemIcon>
            <ArrowDownward />
          </ListItemIcon>
          <ListItemText>{t('admin.customReports.editReports.moveDown')}</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleDeleteSection(sectionIndex);
            setEditDatagridButtonMenuAnchorEl(null);
          }}
          data-testid='delete-section-button'
          disabled={!canDelete}
          sx={errorSx}
        >
          <ListItemIcon>
            <Delete color='error' />
          </ListItemIcon>
          <ListItemText>{t('buttonText.remove')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Edit modal for changing: section name, selected columns, and other options */}
      <Draggable nodeRef={nodeRef} cancel={NonDraggableSelectors}>
        <Popover
          ref={nodeRef}
          slotProps={draggablePopoverBackdropSx}
          anchorEl={editDatagridMenuAnchorEl}
          open={Boolean(editDatagridMenuAnchorEl)}
          onClose={handleCloseEditDatagridMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Grid container direction='column' sx={editMenuContainerLargeSx}>
            <Grid container alignItems='center' justifyContent='space-between' sx={editMenuHalfMarginBelow}>
              <Typography sx={editMenuTitleSx}>{t('admin.customReports.editReports.editData')}</Typography>
              <IconButton
                aria-label={t('buttonText.close')}
                onClick={handleCloseEditDatagridMenu}
                data-testid='edit-menu-dismiss-button'
              >
                <CloseIcon />
              </IconButton>
            </Grid>
            <Typography sx={editMenuMarginBelow}>{t('admin.customReports.editReports.editDataDescription')}</Typography>
            <TextField
              label={t('admin.customReports.editReports.dataTableName')}
              value={editSectionNameValue}
              sx={editMenuMarginBelow}
              onChange={(e) => setEditSectionNameValue(e.target.value)}
              variant='standard'
            />
            <SelectDataset
              selectedOptions={editDatagridValue}
              setSelectedOptions={(opts) => setEditDatagridValue(opts)}
              isEdit={isEditMode}
              selectedDatasetTypeId={selectedDatasetId}
              hideTabs
            />
            <Typography sx={editMenuTitleSx}> {t('admin.centralReportsPage.createReport.defaultSort')} </Typography>
            <Grid container spacing={3} paddingTop={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant='standard' required>
                  <InputLabel id='edit-sort-by-label'> {t('admin.centralReportsPage.createReport.sortBy')} </InputLabel>
                  <Select
                    labelId='edit-sort-by-label'
                    value={editDatagridValue.some((col) => col.label === editSortBy) ? editSortBy : ''}
                    onChange={(e) => setEditSortBy(e.target.value)}
                    disabled={editDatagridValue.length === 0}
                    data-testid='edit-sort-by-select'
                  >
                    {editDatagridValue.map((col) => (
                      <MenuItem key={col.id} value={col.label}>
                        {col.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant='standard' required>
                  <InputLabel id='edit-sort-order-label'>
                    {t('admin.centralReportsPage.createReport.sortOrder')}
                  </InputLabel>
                  <Select
                    labelId='edit-sort-order-label'
                    value={editSortOrder}
                    onChange={(e) => setEditSortOrder(e.target.value as 'asc' | 'desc')}
                    data-testid='edit-sort-order-select'
                  >
                    <MenuItem value='asc'>{t('admin.centralReportsPage.createReport.ascending')}</MenuItem>
                    <MenuItem value='desc'>{t('admin.centralReportsPage.createReport.descending')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Grid container alignItems='center' sx={{ ...gridContainerSx, ...editMenuHalfMarginBelow }}>
              {t('admin.customReports.editReports.showSummaryRow')}
              <Switch
                checked={editShowSummaryRow}
                onChange={(e) => setEditShowSummaryRow(e.target.checked)}
                sx={datasetCheckboxAndSwitchSx(theme, isDarkMode)}
              />
            </Grid>
            <SectionHeaderWithAddButton
              title={t('admin.customReports.editReports.comparisonData')}
              tooltipText={t('admin.customReports.editReports.comparisonDataTooltip')}
              buttonTooltipText={t('admin.customReports.editReports.comparisonDataDisableAddButtonTooltip')}
              isButtonDisabled={availableFactColumnsForComparison.length === 0}
              buttonTestId='add-comparison-button'
              onButtonClick={(e: React.MouseEvent<HTMLButtonElement>) => setAddComparisonMenuAnchorEl(e.currentTarget)}
              theme={theme}
              t={t}
            />
            <Menu
              open={Boolean(addComparisonMenuAnchorEl)}
              anchorEl={addComparisonMenuAnchorEl}
              onClose={() => setAddComparisonMenuAnchorEl(null)}
            >
              {availableFactColumnsForComparison.map((col) => (
                <MenuItem key={col.columnName} onClick={() => handleAddComparativeColumn(col.columnName)}>
                  {col.columnName}
                </MenuItem>
              ))}
            </Menu>
            <Grid container direction='column' gap={1}>
              {comparativeColumns.length === 1 && (
                <ComparativeCard
                  key={comparativeColumns[0].field}
                  showDragIcon={false}
                  col={comparativeColumns[0]}
                  comparisonOptions={getAvailableDateRangesForField(comparativeColumns[0].field, 0)}
                  t={t}
                  setComparativeColumns={setComparativeColumns}
                />
              )}
              {comparativeColumns.length > 1 && (
                <DragDropProvider
                  onDragEnd={(event) => {
                    if (event.canceled) return;

                    // Get field and destination index from the drag event
                    const movingField = event.operation.source?.id;

                    // Reorder the groupedColumns array based on the drag result
                    const newComparativeColumns = [...comparativeColumns];
                    const movingColIndex = newComparativeColumns.findIndex(
                      (col) => `${col.field}_${col.dateRange}` === movingField
                    );
                    const destinationIndex = newComparativeColumns.findIndex(
                      (col) => `${col.field}_${col.dateRange}` === event.operation.target?.id
                    );

                    // Check that the dragged item is valid and has a destination
                    if (movingColIndex === -1 || typeof movingField !== 'string' || destinationIndex === -1) return;

                    const [movingCol] = newComparativeColumns.splice(movingColIndex, 1);
                    newComparativeColumns.splice(destinationIndex, 0, movingCol);

                    // Update state with the new column order
                    setComparativeColumns(newComparativeColumns);
                  }}
                >
                  {comparativeColumns.map((col, index) => (
                    <DroppableZone id={`${col.field}_${col.dateRange}`} key={`droppable-${col.field}-${col.dateRange}`}>
                      <DraggableCard
                        key={`${col.field}-${col.dateRange}`}
                        type='comparative'
                        col={col}
                        options={getAvailableDateRangesForField(col.field, index)}
                        t={t}
                        setState={setComparativeColumns}
                      />
                    </DroppableZone>
                  ))}
                </DragDropProvider>
              )}
            </Grid>

            <SectionHeaderWithAddButton
              title={t('admin.customReports.editReports.groupingByRows')}
              tooltipText={t('admin.customReports.editReports.groupingHeaderTooltip')}
              buttonTooltipText={t('admin.customReports.editReports.groupingDisableAddButtonTooltip')}
              isButtonDisabled={
                dimensionColumns.filter((col) => !groupedColumns.some((dimCol) => dimCol.field === col.field))
                  .length === 0
              }
              buttonTestId='add-grouping-button'
              onButtonClick={(e: React.MouseEvent<HTMLButtonElement>) => setAddGroupingMenuAnchorEl(e.currentTarget)}
              theme={theme}
              t={t}
            />
            <Menu
              open={Boolean(addGroupingMenuAnchorEl)}
              anchorEl={addGroupingMenuAnchorEl}
              onClose={() => setAddGroupingMenuAnchorEl(null)}
            >
              {dimensionColumns
                .filter((col) => !groupedColumns.some((dimCol) => dimCol.field === col.field))
                .map((col) => (
                  <MenuItem key={col.field} onClick={() => handleAddGroupingColumn(col.field)}>
                    {col.field}
                  </MenuItem>
                ))}
            </Menu>
            <Grid container direction='column' gap={1}>
              {groupedColumns.length === 1 && (
                <GroupingCard col={groupedColumns[0]} showDragIcon={false} setGroupedColumns={setGroupedColumns} />
              )}
              {groupedColumns.length > 1 && (
                <DragDropProvider
                  onDragEnd={(event) => {
                    if (event.canceled) return;

                    // Get field and destination index from the drag event
                    const movingField = event.operation.source?.id;

                    // Reorder the groupedColumns array based on the drag result
                    const newGroupedColumns = [...groupedColumns];
                    const movingColIndex = newGroupedColumns.findIndex((col) => col.field === movingField);
                    const destinationIndex = newGroupedColumns.findIndex(
                      (col) => col.field === event.operation.target?.id
                    );

                    // Check that the dragged item is valid and has a destination
                    if (movingColIndex === -1 || typeof movingField !== 'string' || destinationIndex === -1) return;

                    const [movingCol] = newGroupedColumns.splice(movingColIndex, 1);
                    newGroupedColumns.splice(destinationIndex, 0, movingCol);

                    // Update state with the new column order
                    setGroupedColumns(newGroupedColumns);
                  }}
                >
                  {groupedColumns.map((col, index) => (
                    <DroppableZone id={`${col.field}`} key={`droppable-${col.field}`}>
                      <DraggableCard key={col.field} type='group' col={col} setState={setGroupedColumns} />
                    </DroppableZone>
                  ))}
                </DragDropProvider>
              )}
            </Grid>
          </Grid>
        </Popover>
      </Draggable>

      <Grid
        container
        data-testid={`datagrid-section-${sectionIndex + 1}`}
        key={`datagrid-section-${sectionIndex + 1}`}
        direction='column'
        sx={addSectionButtonContainerSx(isActive)}
        onMouseOver={handleOnMouseOver}
        onMouseLeave={handleOnMouseLeave}
        onFocus={handleOnMouseOver}
        onBlur={handleOnBlur}
      >
        {isActive && addSectionButton('above')}
        <Grid container sx={isActive ? customReportDataGridOutlineSx : customReportDataGridContainerSx(isEditMode)}>
          <Grid justifyContent='space-between' container sx={editMenuHalfMarginBelow}>
            {(sectionName || isEditMode) && (
              <Grid item sx={sectionNameContainerSx}>
                <Typography variant='h6'>
                  {sectionName ?? t('admin.customReports.editReports.tableTitle', { number: sectionIndex + 1 })}
                </Typography>
              </Grid>
            )}
            {isActive && (
              <IconButton
                onClick={(e) => handleOpenEditDatagridMenu(e)}
                id={`edit-datagrid-button-${sectionIndex + 1}`}
                data-testid='custom-reports-edit-datagrid-button'
              >
                <EditIcon />
              </IconButton>
            )}
          </Grid>
          <Grid width='100%'>
            <DataGrid
              treeData={groupedColumns.length > 0}
              groupingColDef={groupingColDef}
              getTreeDataPath={getTreeDataPath}
              defaultGroupingExpansionDepth={1}
              rows={rows ?? []}
              columns={filteredColumns ?? []}
              sx={customReportDataGridSx(isEditMode)}
              loading={isExecuteCustomReportLoading}
              autoHeight={!isEditMode}
              disableAggregation={false}
              hideFromColumnSelector={[GRID_TREE_DATA_GROUPING_FIELD]}
              {...(isEditMode
                ? {
                    onPageChange: (newPage) => setPaginationModel((prev) => ({ ...prev, page: newPage })),
                    paginationModel,
                    pageSizeOptions: [3],
                    loadingRowCount: 3,
                  }
                : {})}
              aggregationModel={showSummaryRow ? aggregationModelWithComparative : {}}
              exportCSV={exportCustomReportsGridData}
              isExportLoading={exportCustomReportMutation.isLoading}
              showExport={!isEditMode && rows && rows.length > 0}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
            />
          </Grid>
        </Grid>
        {isActive && addSectionButton('below')}
      </Grid>
    </>
  );
}

export default CustomReportsGridSection;
